import { IColumn, IDetailsRowProps, IObjectWithKey } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPScrollablePane, IPSearchBox, IPShimmeredDetailsList, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode, IPSelection } from 'baseComponents/Utils/Selection';
import { ClassificationTagList } from 'components/Classification/ClassificationTagList';
import HrefLink from 'components/Links/HrefLink';
import { OwnerItem } from 'components/Pickers/OwnerPicker';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { globalFilterDelay } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalSearchBoxStyles, globalStackItemStylesPaddingSceneScroll } from 'globalStyles';
import { TFunction } from 'i18next';
import Asset from 'models/asset/asset';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copyAndSortAssets } from './AssetListSorting';

interface IAssetListProps {
  assets: Asset[];
  isLoading?: boolean;
  showSearch?: boolean;
  onSelectAssets?: (items: Asset[]) => void;
  onRemove?: (item: Asset) => void;
  onListClick?: (item: Asset) => void;
  existingAssets?: number[];
  allowAssetNavigation?: boolean;
  showLocationColumn?: boolean;
}

const AssetList = (props: IAssetListProps) => {
  const { t } = useTranslation(['translation', 'assets', 'asset']);
  const appContext = useContext(AppContext);
  const [assetsDisplay, setAssetsDisplay] = useState<Asset[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [selection] = useState<IPSelection<Asset>>(
    new IPSelection({
      selectionMode: props.onSelectAssets ? IPSelectionMode.multiple : IPSelectionMode.none,
      canSelectItem: (item) => {
        return !props.existingAssets?.includes(item.assetId);
      },
      getKey: (item: Asset) => {
        return item.assetId;
      },
      onSelectionChanged: () => {
        const sel = selection.getSelection() as Asset[];
        if (props.onSelectAssets) {
          props.onSelectAssets(sel);
        }
      },
    }),
  );

  //
  // Effects
  //
  useEffect(() => {
    setAssetsDisplay(props.assets);

    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setAssetsDisplay(props.assets);
    } else {
      const queryLower = searchQuery.toLowerCase();
      const filtered = props.assets.filter((entry) => {
        if (entry.listId) {
          const listName = entry.getListTypeText(true, appContext, t as TFunction<string[]>);
          if (listName && listName.toLowerCase().includes(queryLower)) {
            return true;
          }
        }

        return Object.values(entry).some((val) => {
          if (typeof val === 'string') {
            if (entry.name && entry.name.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.code && entry.code.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
              return true;
            }
          }

          return false;
        });
      });

      if (filtered) {
        setAssetsDisplay(filtered);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.assets, searchQuery]);

  //
  // Columns list view
  //
  const getColumnsList = (): IColumn[] => {
    const locationColumn = {
      key: 'location',
      name: t('assets:TabOverView.Columns.Location'),
      minWidth: 150,
      maxWidth: 250,
      isResizable: true,
      onRender: (item?: Asset) => <IPText>{item?.location?.value}</IPText>,
    };

    const allColumns = [
      {
        key: 'code',
        name: t('assets:TabOverView.Columns.Code'),
        minWidth: 100,
        maxWidth: 100,
        isSorted: true,
        isSortedDescending: false,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.code}</IPText>;
        },
      },
      {
        key: 'name',
        name: t('assets:TabOverView.Columns.Name'),
        minWidth: 150,
        maxWidth: 400,
        isRowHeader: true,
        isResizable: true,
        isMultiline: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          if (!item) return;

          if (props.allowAssetNavigation) {
            return (
              <IPText>
                <HrefLink href={`/asset/${item.assetId}/details`} underline>
                  {item.name}
                </HrefLink>
              </IPText>
            );
          } else {
            return <IPText>{item.name}</IPText>;
          }
        },
      },
      {
        key: 'type',
        name: t('assets:TabOverView.Columns.ListType'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          if (!item) return;

          return (
            <IPText nowrap>
              <IPLink
                onClick={() => {
                  if (props.onListClick) props.onListClick(item);
                }}
              >
                {item?.getListTypeText(false, appContext, t as TFunction<string[]>)}
              </IPLink>
            </IPText>
          );
        },
      },
      {
        key: 'state',
        name: t('assets:TabOverView.Columns.State'),
        minWidth: 100,
        maxWidth: 100,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <IPText>{item?.getStateText(t as TFunction<string[]>)}</IPText>;
        },
      },
      ...(props.showLocationColumn ? [locationColumn] : []),
      {
        key: 'tags',
        name: t('assets:TabOverView.Columns.Tags'),
        minWidth: 200,
        maxWidth: 400,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <KeyValueTags tags={appContext.globalDataCache.tags.getItemsForId(item?.tagIds)} />;
        },
      },
      {
        key: 'classifications',
        name: t('assets:TabOverView.Columns.Classifications'),
        minWidth: 230,
        maxWidth: 400,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <ClassificationTagList classifications={item?.classifications ?? []} />;
        },
      },
      {
        key: 'owner',
        name: t('assets:TabOverView.Columns.Owner'),
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (item?: Asset, index?: number, column?: IColumn) => {
          return <OwnerItem owner={item} />;
        },
      },
    ];

    return allColumns;
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumnsList());

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  const onRenderRow = (
    props?: IDetailsRowProps,
    defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null,
  ): JSX.Element | null => {
    if (defaultRender && props?.item && !selection.canSelectItem(props.item)) {
      props.disabled = true;

      return (
        <IPTooltipHost content={t('assets:Picker.ItemExists')} directionalHint={IPDirectionalHint.topLeftEdge}>
          {defaultRender(props)}
        </IPTooltipHost>
      );
    }

    if (defaultRender) {
      return defaultRender(props);
    } else {
      return null;
    }
  };

  const onColumnClick = (
    ev?: React.MouseEvent<HTMLElement>,
    column?: IColumn,
    isForceDescendingEnabled?: boolean,
  ): void => {
    if (!column) return;

    const newColumns: IColumn[] = columnsList.slice(); //new columns
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    if (isForceDescendingEnabled !== undefined) {
      newColumns.forEach((newCol: IColumn) => {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = isForceDescendingEnabled;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = true;
        }
      });
    } else {
      for (const newCol of newColumns) {
        if (newCol === currColumn) {
          currColumn.isSortedDescending = !currColumn.isSortedDescending;
          currColumn.isSorted = true;
        } else {
          newCol.isSorted = false;
          newCol.isSortedDescending = false;
        }
      }
    }

    const newItems = copyAndSortAssets(props.assets, currColumn, currColumn.isSortedDescending, appContext);

    setColumnsList(newColumns);
    setAssetsDisplay(newItems);
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {props.showSearch && (
        <IPStack.Item>
          <IPSearchBox
            autoFocus={!appContext.isMobileView}
            underlined
            placeholder={t('translation:General.Filter.Placeholder')}
            id="search"
            onChange={(ev, newValue) => {
              if (searchTimer) {
                clearTimeout(searchTimer);
              }
              setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
            }}
            styles={globalSearchBoxStyles}
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            compact
            enableShimmer={props.isLoading}
            selection={selection as IPSelection<IObjectWithKey>}
            items={assetsDisplay}
            isHeaderVisible={true}
            columns={columnsList}
            setKey="set"
            selectionMode={IPSelectionMode.multiple}
            layoutMode={IPDetailsListLayoutMode.justified}
            selectionPreservedOnEmptyClick={true}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            onRenderRow={onRenderRow}
            onColumnHeaderClick={onColumnClick}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default AssetList;
